import React, { useState, useEffect, useCallback } from 'react';
import { Robot, SpinnerGap, Check } from '@phosphor-icons/react';
import * as api from '../../api/client';

interface WorkspaceAIPanelProps {
  workspaceId: string | null;
}

const MODEL_PRESETS: { value: string; label: string }[] = [
  { value: '', label: 'Default (claude-sonnet-4-6)' },
  { value: 'claude-opus-4-7', label: 'Opus 4.7 (most capable)' },
  { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6 (balanced)' },
  { value: 'claude-haiku-4-5', label: 'Haiku 4.5 (fastest)' },
];

export default function WorkspaceAIPanel({ workspaceId }: WorkspaceAIPanelProps) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<api.WorkspaceSettings | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pendingKey, setPendingKey] = useState('');
  const [pendingModel, setPendingModel] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [savedField, setSavedField] = useState<'key' | 'model' | null>(null);

  const reload = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const res = await api.getWorkspaceSettings(workspaceId);
      setSettings(res.settings);
      setPendingModel(res.settings.anthropic_model || '');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (/403|forbidden|owner/i.test(msg)) {
        setForbidden(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const flashSaved = useCallback((field: 'key' | 'model') => {
    setSavedField(field);
    setTimeout(() => setSavedField(prev => (prev === field ? null : prev)), 2000);
  }, []);

  const saveKey = useCallback(async () => {
    if (!workspaceId || !pendingKey.trim()) return;
    setSavingKey(true);
    setError(null);
    try {
      const res = await api.updateWorkspaceSettings(workspaceId, {
        anthropic_api_key: pendingKey.trim(),
      });
      setSettings(res.settings);
      setPendingKey('');
      flashSaved('key');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save key.');
    } finally {
      setSavingKey(false);
    }
  }, [workspaceId, pendingKey, flashSaved]);

  const clearKey = useCallback(async () => {
    if (!workspaceId) return;
    setSavingKey(true);
    setError(null);
    try {
      const res = await api.updateWorkspaceSettings(workspaceId, {
        anthropic_api_key: null,
      });
      setSettings(res.settings);
      setPendingKey('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear key.');
    } finally {
      setSavingKey(false);
    }
  }, [workspaceId]);

  const saveModel = useCallback(async () => {
    if (!workspaceId) return;
    const v = pendingModel.trim();
    if ((settings?.anthropic_model || '') === v) return;
    setSavingModel(true);
    setError(null);
    try {
      const res = await api.updateWorkspaceSettings(workspaceId, {
        anthropic_model: v || null,
      });
      setSettings(res.settings);
      flashSaved('model');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save model.');
    } finally {
      setSavingModel(false);
    }
  }, [workspaceId, pendingModel, settings, flashSaved]);

  const sectionStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '20px 24px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--fs-sm)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 8,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: 'var(--fs-base)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };

  const button = (primary: boolean, disabled: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    fontSize: 'var(--fs-sm)',
    fontWeight: 600,
    border: primary ? 'none' : '1px solid var(--border)',
    borderRadius: 8,
    background: primary ? 'var(--brand)' : 'var(--bg-primary)',
    color: primary ? 'var(--text-on-brand)' : 'var(--text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'opacity 0.15s',
  });

  if (!workspaceId) return null;

  if (loading) {
    return (
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
          <SpinnerGap size={16} weight="light" className="animate-spin" />
          <span style={{ fontSize: 'var(--fs-sm)' }}>Loading workspace AI settings…</span>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Robot size={16} weight="light" style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>
            Workspace AI
          </span>
        </div>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: 0 }}>
          Only the workspace owner can configure the Anthropic API key. Ask the owner of this workspace
          to set it under Settings.
        </p>
      </div>
    );
  }

  const hasKey = settings?.has_anthropic_key === true;
  const updatedLabel = settings?.updated_at
    ? new Date(settings.updated_at).toLocaleString()
    : null;

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Robot size={16} weight="light" style={{ color: 'var(--text-muted)' }} />
        <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>
          Workspace AI
        </span>
      </div>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Anthropic API key used by every deck agent in this workspace. Stored encrypted; never echoed back.
      </p>

      {/* Anthropic API key */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={labelStyle}>Anthropic API Key</div>
          <a
            href="https://console.anthropic.com/settings/keys"
            target="_blank"
            rel="noreferrer noopener"
            style={{ fontSize: 'var(--fs-xs)', color: 'var(--brand)', textDecoration: 'none' }}
          >
            Get a key ↗
          </a>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="password"
            value={pendingKey}
            placeholder={hasKey ? '••••••••  (set — re-enter to update)' : 'sk-ant-...'}
            onChange={e => setPendingKey(e.target.value)}
            style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }}
          />
          <button
            disabled={savingKey || !pendingKey.trim()}
            onClick={() => void saveKey()}
            style={button(true, savingKey || !pendingKey.trim())}
          >
            {savingKey ? <SpinnerGap size={14} weight="light" className="animate-spin" /> : null}
            {savedField === 'key' ? <Check size={14} weight="bold" /> : null}
            {savedField === 'key' ? 'Saved' : 'Save'}
          </button>
          {hasKey && (
            <button
              disabled={savingKey}
              onClick={() => void clearKey()}
              style={button(false, savingKey)}
              title="Clear the saved key. Worker fallback (env var) will be used instead."
            >
              Clear
            </button>
          )}
        </div>
        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', margin: '6px 0 0' }}>
          {hasKey
            ? `Key is set${updatedLabel ? ` (last updated ${updatedLabel})` : ''}. Used by deck agents in this workspace.`
            : 'No workspace key set. Deck agents fall back to the worker-wide ANTHROPIC_API_KEY env var if available.'}
        </p>
      </div>

      {/* Model */}
      <div>
        <div style={labelStyle}>Model</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={pendingModel}
            onChange={e => setPendingModel(e.target.value)}
            style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}
          >
            {MODEL_PRESETS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <button
            disabled={savingModel || pendingModel.trim() === (settings?.anthropic_model || '')}
            onClick={() => void saveModel()}
            style={button(true, savingModel || pendingModel.trim() === (settings?.anthropic_model || ''))}
          >
            {savingModel ? <SpinnerGap size={14} weight="light" className="animate-spin" /> : null}
            {savedField === 'model' ? <Check size={14} weight="bold" /> : null}
            {savedField === 'model' ? 'Saved' : 'Save'}
          </button>
        </div>
        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', margin: '6px 0 0' }}>
          Overrides the worker-wide default. Leave on Default to inherit the deploy.
        </p>
      </div>

      {error && (
        <div style={{
          marginTop: 16,
          padding: '8px 12px',
          background: 'var(--ds-danger-soft)',
          border: '1px solid var(--ds-danger-soft)',
          borderRadius: 6,
          color: 'var(--ds-danger)',
          fontSize: 'var(--fs-sm)',
        }}>
          {error}
        </div>
      )}
    </div>
  );
}
